import { useEffect, useRef, useState } from 'react';
import { fetchUploads, uploadImage, deleteUpload, renameUpload } from '../../services/adminApi';
import Button from '../../components/admin/ui/Button';
import Loader from '../../components/admin/ui/Loader';
import ImagePreview from '../../components/admin/ui/ImagePreview';

function AdminLibraryPage({ showToast }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      setUploads((await fetchUploads()) || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await uploadImage(file);
      }
      showToast('Fichier(s) ajouté(s) à la bibliothèque.');
      await reload();
    } catch (error) {
      showToast('Erreur lors du téléversement.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (upload) => {
    if (!window.confirm(`Supprimer "${upload.originalName}" de la bibliothèque ?`)) return;
    await deleteUpload(upload.id);
    setUploads((prev) => prev.filter((u) => u.id !== upload.id));
    showToast('Fichier supprimé.');
  };

  const handleRename = async (upload) => {
    const nextName = window.prompt('Nouveau nom du fichier :', upload.originalName);
    if (!nextName || !nextName.trim() || nextName === upload.originalName) return;
    try {
      const updated = await renameUpload(upload.id, nextName.trim());
      setUploads((prev) => prev.map((u) => (u.id === upload.id ? updated : u)));
      showToast('Fichier renommé.');
    } catch (error) {
      showToast('Erreur lors du renommage.');
    }
  };

  if (loading) return <Loader label="Chargement de la bibliothèque..." />;

  return (
    <section className="admin-section">
      <div className="admin-actions" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <div>
          <h2>Bibliothèque</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
            {uploads.length} fichier{uploads.length > 1 ? 's' : ''} — utilisés dans les formulaires (logos, icônes, galeries).
          </p>
        </div>
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? 'Envoi...' : '+ Ajouter des fichiers'}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} />
      </div>

      {uploads.length === 0 ? (
        <p>Aucun fichier dans la bibliothèque.</p>
      ) : (
        <div className="admin-asset-grid">
          {uploads.map((upload) => (
            <div className="admin-asset-item" key={upload.id}>
              <div className="admin-asset-pick">
                <ImagePreview src={`/uploads/${upload.filename}`} size={72} />
                <span className="admin-asset-name" title={upload.originalName}>
                  {upload.originalName}
                </span>
              </div>
              <div className="admin-library-item-actions">
                <button type="button" className="admin-library-action" onClick={() => handleRename(upload)}>
                  Renommer
                </button>
                <button
                  type="button"
                  className="admin-library-action admin-library-action-danger"
                  onClick={() => handleDelete(upload)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default AdminLibraryPage;
